/**
 * Admin mode — visual debugging overlays for trigger selectors.
 * Activated via ?bh_admin=1 query param or localStorage bh_admin flag.
 *
 * Bookmarklet to toggle:
 *   javascript:void(localStorage.getItem('bh_admin')?localStorage.removeItem('bh_admin'):localStorage.setItem('bh_admin','1'),location.reload())
 */

import type { Trigger } from "./messages";

declare global {
  interface Window {
    __bhScrollMarkers?: Map<string, HTMLElement>;
  }
}

const STORAGE_KEY = "bh_admin";

export function isAdminMode(): boolean {
  try {
    // Query param enables and persists; localStorage keeps it across navigation
    const params = new URLSearchParams(window.location.search);
    if (params.get(STORAGE_KEY) === "1") {
      localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (params.get(STORAGE_KEY) === "0") {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

const COLORS = {
  dwell: "#2563eb",     // blue-600
  click: "#16a34a",     // green-600
  form: "#ea580c",      // orange-600
  scroll_depth: "#9333ea", // purple-600
} as const;

const LABELS = {
  dwell: "DWELL",
  click: "CLICK",
  form: "FORM",
  scroll_depth: "SCROLL",
} as const;

export function injectAdminStyles(triggers: Trigger[]): () => void {
  const rules: string[] = [];
  const cleanups: (() => void)[] = [];

  // @property prevents CSS var inheritance — body's triggered vars
  // won't cascade to child elements
  rules.push(`@property --bh-outline-style {
  syntax: "*";
  inherits: false;
}
@property --bh-outline-color {
  syntax: "*";
  inherits: false;
}
@property --bh-outline-width {
  syntax: "*";
  inherits: false;
}`);

  const elementTriggers = triggers.filter((t) => t.type !== "scroll_depth");
  const scrollTriggers = triggers.filter((t) => t.type === "scroll_depth");

  for (const trigger of elementTriggers) {
    const color = COLORS[trigger.type];
    const sel = trigger.type === "form"
      ? (trigger.selector || "form")
      : trigger.selector;

    rules.push(`${sel} {
  outline: var(--bh-outline-width, 3px) var(--bh-outline-style, dashed) var(--bh-outline-color, ${color}) !important;
  outline-offset: 2px !important;
  position: relative !important;
}`);
  }

  // Triggered state — vars flip to solid green + badge update
  rules.push(`.bh-triggered {
  --bh-outline-style: solid;
  --bh-outline-color: #16a34a;
  --bh-outline-width: 4px;
}
.bh-triggered > .bh-admin-label {
  background: #16a34a !important;
}
.bh-triggered > .bh-admin-label::after {
  content: " \\2714" !important;
}`);

  const style = document.createElement("style");
  style.setAttribute("data-bluehat-admin", "");
  style.textContent = rules.join("\n");
  document.head.appendChild(style);
  cleanups.push(() => style.remove());

  // Add floating labels to element-based triggers
  for (const trigger of elementTriggers) {
    const color = COLORS[trigger.type];
    const label = LABELS[trigger.type];
    const sel = trigger.type === "form"
      ? (trigger.selector || "form")
      : trigger.selector;

    for (const el of document.querySelectorAll(sel)) {
      const badge = document.createElement("div");
      badge.className = "bh-admin-label";
      badge.textContent = `${label}: ${trigger.id}`;
      badge.style.cssText = `
        position: absolute;
        top: -1px;
        left: -1px;
        transform: translateY(-100%);
        background: ${color};
        color: #fff;
        font: bold 10px/1 system-ui, sans-serif;
        padding: 2px 6px;
        border-radius: 3px 3px 0 0;
        z-index: 2147483647;
        pointer-events: none;
        white-space: nowrap;
      `;
      (el as HTMLElement).style.position = "relative";
      el.appendChild(badge);
      cleanups.push(() => badge.remove());
    }
  }

  // Fixed scroll depth meter on right edge
  if (scrollTriggers.length > 0) {
    const meter = document.createElement("div");
    meter.setAttribute("data-bluehat-scroll-meter", "");
    meter.style.cssText = `
      position: fixed;
      right: 0;
      top: 0;
      width: 24px;
      height: 100vh;
      height: 100dvh; /* dynamic viewport on mobile; browsers without dvh keep the 100vh line */
      z-index: 2147483647;
      pointer-events: none;
    `;

    // Track bar showing current scroll position
    const track = document.createElement("div");
    track.style.cssText = `
      position: absolute;
      right: 0;
      top: 0;
      width: 4px;
      height: 100%;
      background: rgba(147, 51, 234, 0.15);
    `;
    meter.appendChild(track);

    // Marker for each depth threshold
    const markers = new Map<string, HTMLElement>();
    for (const trigger of scrollTriggers) {
      const marker = document.createElement("div");
      marker.setAttribute("data-bh-scroll-id", trigger.id);
      marker.style.cssText = `
        position: absolute;
        right: 0;
        top: ${trigger.depth}%;
        transform: translateY(-50%);
        background: ${COLORS.scroll_depth};
        color: #fff;
        font: bold 9px/1 system-ui, sans-serif;
        padding: 2px 5px;
        border-radius: 3px 0 0 3px;
        white-space: nowrap;
        transition: background 0.2s;
      `;
      marker.textContent = `${trigger.depth}%`;
      meter.appendChild(marker);
      markers.set(trigger.id, marker);
    }

    document.body.appendChild(meter);
    cleanups.push(() => meter.remove());

    // Store markers for markTriggered to update
    window.__bhScrollMarkers = markers;
  }

  return () => {
    for (const fn of cleanups) fn();
    window.__bhScrollMarkers = undefined;
  };
}

export function markTriggered(triggerId: string, triggers: Trigger[]): void {
  const trigger = triggers.find((t) => t.id === triggerId);
  if (!trigger) return;

  if (trigger.type === "scroll_depth") {
    // Update the fixed scroll meter marker
    const markers = window.__bhScrollMarkers;
    const marker = markers?.get(triggerId);
    if (marker) {
      marker.style.background = "#16a34a";
      marker.textContent = `${trigger.depth}% \u2714`;
    }
    return;
  }

  let selector: string;
  switch (trigger.type) {
    case "dwell":
    case "click":
      selector = trigger.selector;
      break;
    case "form":
      selector = trigger.selector || "form";
      break;
  }

  for (const el of document.querySelectorAll(selector)) {
    el.classList.add("bh-triggered");
  }
}
